import { LongLayoverIcon, ShortLayoverIcon } from '@/src/assets/svgs/LayoverIcons'
import DateCarouselSkeleton from '@/src/components/desktop/srp/dateCarousel/DateCarouselSkeleton'
import { useSrpContext } from '@/src/contexts'
import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import { useFormatTime } from '@/src/hooks/srp/formatTime'
import { getAirlineIcons } from '@/src/utils/confirmation/airlineUtils'
import CircleIcon from '@mui/icons-material/Circle'
import { Box, Button, Card, CardContent, Skeleton, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'

const containerBoxSx = { p: 2, border: '1px solid #e0e0e0', backgroundColor: '#fff', borderRadius: 1.5 }
const headerBoxSx = {
    py: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: 1,
    borderColor: 'grey.200'
}
const routeTypographySx = { fontWeight: 600, color: 'grey.700' }
const innerListContainerSx = { px: 2, mt: 2 }
const cardWrapperSx = { pb: 1 }
const mainFlightBoxSx = { px: 3, pt: 1.5, pb: 1.5 }

const cardSx = { borderRadius: 1, border: '1px solid #e0e0e0', overflow: 'hidden', boxShadow: 'none', p: 0, mx: 0 }

// Keep the loading result list at least this tall (in cards) so that when client
// data arrives with more cards, content below (page content/FAQ/footer) is not
// pushed down into the viewport, which would cause layout shift (CLS).
const MIN_VISIBLE_CARDS = 5

const SkeletonFlightCard = () => (
    <Box component="li" sx={cardWrapperSx}>
        <Card sx={{ ...cardSx, position: 'relative' }}>
            <Box sx={{ ...mainFlightBoxSx, pt: 1.5 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    {/* Airline */}
                    <Box display="flex" flexDirection="column" alignItems="center" width="100px" gap={0.5}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="text" width={70} height={14} />
                    </Box>
                    {/* Flight info */}
                    <Box flex={1}>
                        <Skeleton variant="text" width="60%" height={20} />
                        <Skeleton variant="text" width="40%" height={16} />
                    </Box>
                    {/* Price + action */}
                    <Box display="flex" alignItems="center" gap={2} flexShrink={0}>
                        <Skeleton variant="text" width={70} height={20} />
                        <Skeleton variant="rounded" width={72} height={40} />
                    </Box>
                </Box>
            </Box>
        </Card>
    </Box>
)

const SeoFlightResult = () => {
    const { formatCurrency } = useFormatCurrencyForSrp()
    const {
        SEOSrpData: { card: oneWayCards, searchQuery, json }
    } = useSrpContext()
    const formatTime = useFormatTime()
    const { origin, destination } = searchQuery || {}
    const { airline_names } = json || {}
    // const oneWayCards = cards?.[0]
    const tCommon = useTranslations('common')
    const t = useTranslations('srpFlightDetails')
    function getLayoverTypeAndMessage (layoverMinutes: number) {
        if (layoverMinutes > 240) {
            return {
                type: 'longLayover',
                message: t('longLayoverCta'),
                iconColor: 'warning.main'
            }
        }
        return {
            type: 'shortLayover',
            message: t('shortLayoverCta'),
            iconColor: 'info.main'
        }
    }
    const cards = oneWayCards?.slice(0, MIN_VISIBLE_CARDS) ?? []
    const skeletonCount = Math.max(0, MIN_VISIBLE_CARDS - cards.length)
    return (
        <Box component="section" aria-label="Flight results list" sx={containerBoxSx}>
            <Box sx={headerBoxSx}>
                <Typography component="h2" variant="subtitle2" sx={routeTypographySx}>
                    {origin} → {destination}
                </Typography>
            </Box>

            {/* Reserve the date carousel space so the real carousel doesn't push
                the list and content below it down once client data loads (CLS). */}
            <DateCarouselSkeleton />

            <Box component="ul" sx={{ ...innerListContainerSx, listStyle: 'none', m: 0, p: 0 }}>
                {cards.map((card, index) => {
                    const { from, to } = card
                    const duration = formatTime((card?.totalDuration || 0) * 60) //total duration in minutes and formatTime need seconds as input
                    const strikeOffPrice = card?.discountAmount + card?.price
                    return (
                        <Box component="li" key={index} sx={cardWrapperSx}>
                            <Card component="article" sx={{ ...cardSx, position: 'relative' }}>
                                <Box sx={{ ...mainFlightBoxSx, pt: 1.5 }}>
                                    <Box
                                        sx={{
                                            p: 0,
                                            bgcolor: 'white',
                                            cursor: 'pointer',

                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                borderColor: 'grey.300'
                                            }
                                        }}
                                        width={'820px'}
                                    >
                                        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, minWidth: 'max-content' }}>
                                            <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                                                <Box display="flex" alignItems="center" gap={1.5} flex={1}>
                                                    {/* Airline logo */}
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        flexDirection="column"
                                                        width="100px"
                                                        sx={{ overflowX: 'wrap', wordBreak: 'break-word' }}
                                                    >
                                                        <Box
                                                            display={'flex'}
                                                            maxWidth={150}
                                                            overflow={'auto'}
                                                            gap={0.5}
                                                        >
                                                            {getAirlineIcons([card?.airline], 32, 32)}
                                                        </Box>
                                                        <Typography textAlign="center" variant="body2" color="grey.600">
                                                            {airline_names?.[card?.airline] ?? card?.airline}
                                                        </Typography>
                                                    </Box>

                                                    {/* Flight info */}
                                                    <Box flex={1}>
                                                        {/* Time and route */}
                                                        <Box display="flex" alignItems="flex-end" gap={1}>
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                flexDirection="column"
                                                                width="60px"
                                                            >
                                                                <Typography
                                                                    variant="h6"
                                                                    component="span"
                                                                    sx={{
                                                                        fontSize: '1.125rem',
                                                                        fontWeight: 700,
                                                                        color: 'black',
                                                                        display: 'flex',
                                                                        flexDirection: 'row'
                                                                    }}
                                                                >
                                                                    {card?.departureTime}
                                                                </Typography>

                                                                <Typography
                                                                    variant="body2"
                                                                    fontWeight={600}
                                                                    sx={{
                                                                        color: 'grey.600',
                                                                        display: 'inline-block'
                                                                    }}
                                                                >
                                                                    {from}
                                                                </Typography>
                                                            </Box>
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                flexDirection="column"
                                                            >
                                                                <Typography
                                                                    fontSize="12px"
                                                                    variant="body2"
                                                                    color="grey.600"
                                                                >
                                                                    {duration}
                                                                </Typography>
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        mx: 1
                                                                    }}
                                                                >
                                                                    <CircleIcon
                                                                        sx={{ fontSize: 6, color: 'grey.600' }}
                                                                    />
                                                                    <Box
                                                                        height="1px"
                                                                        width="142px"
                                                                        bgcolor="grey.600"
                                                                    />
                                                                    <CircleIcon
                                                                        sx={{ fontSize: 6, color: 'grey.600' }}
                                                                    />
                                                                </Box>
                                                                <Box
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    flexDirection="row"
                                                                >
                                                                    <Typography
                                                                        fontSize="12px"
                                                                        variant="body2"
                                                                        color={
                                                                            card?.maxStopsInSectors === 0
                                                                                ? ' #16a34a'
                                                                                : 'grey.600'
                                                                        }
                                                                        fontWeight={
                                                                            card?.maxStopsInSectors === 0 ? 600 : 400
                                                                        }
                                                                    >
                                                                        {card?.maxStopsInSectors === 0
                                                                            ? tCommon('direct')
                                                                            : `${card?.maxStopsInSectors} ${card?.maxStopsInSectors > 1 ? tCommon('stops', { s: 's' }) : tCommon('stops', { s: '' })}`}
                                                                    </Typography>
                                                                    {card?.layOverAirport && (
                                                                        <>
                                                                            <CircleIcon
                                                                                sx={{
                                                                                    fontSize: 5,
                                                                                    color: 'grey.600',
                                                                                    mx: 0.5
                                                                                }}
                                                                            />
                                                                            <Typography
                                                                                component="span"
                                                                                sx={{
                                                                                    fontSize: '12px',
                                                                                    fontWeight: 400,
                                                                                    color: 'textColors.primary'
                                                                                }}
                                                                            >
                                                                                {card?.layOverAirport}
                                                                            </Typography>
                                                                        </>
                                                                    )}
                                                                    {/* Layover Icon (if present) */}
                                                                    {card?.layOverAirport &&
                                                                        (() => {
                                                                            const { type } = getLayoverTypeAndMessage(
                                                                                card?.layovertime
                                                                            )
                                                                            const LayoverIcon =
                                                                                type === 'longLayover'
                                                                                    ? LongLayoverIcon
                                                                                    : ShortLayoverIcon
                                                                            return (
                                                                                <Box
                                                                                    display="inline-flex"
                                                                                    alignItems="center"
                                                                                    sx={{ cursor: 'help', ml: 1 }}
                                                                                >
                                                                                    <LayoverIcon />
                                                                                </Box>
                                                                            )
                                                                        })()}
                                                                </Box>
                                                            </Box>
                                                            <Box display="flex">
                                                                <Box
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    flexDirection="column"
                                                                    width="60px"
                                                                >
                                                                    <Typography
                                                                        variant="h6"
                                                                        component="span"
                                                                        sx={{
                                                                            fontSize: '1.125rem',
                                                                            fontWeight: 700,
                                                                            color: 'black',
                                                                            display: 'flex',
                                                                            flexDirection: 'row'
                                                                        }}
                                                                    >
                                                                        {card?.arrivalTime}
                                                                    </Typography>

                                                                    <Typography
                                                                        variant="body2"
                                                                        fontWeight={600}
                                                                        sx={{
                                                                            color: 'grey.600',
                                                                            display: 'inline-block'
                                                                        }}
                                                                    >
                                                                        {to}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </Box>

                                                {/* Right section - Price and actions */}
                                                <Box textAlign="right" ml={3} flexShrink={0}>
                                                    <Box display="flex" alignItems="center" gap={2} mb={0.5}>
                                                        {/* Price section */}
                                                        <Box textAlign="right">
                                                            {typeof strikeOffPrice === 'number' &&
                                                                strikeOffPrice > Number(card?.price) && (
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: 'grey.500',
                                                                            textDecoration: 'line-through',
                                                                            fontWeight: 400,
                                                                            fontSize: '0.75rem',
                                                                            mb: 0.5
                                                                        }}
                                                                    >
                                                                        {formatCurrency(strikeOffPrice)}
                                                                    </Typography>
                                                                )}
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                gap={1}
                                                                justifyContent={'flex-end'}
                                                            >
                                                                <Typography
                                                                    variant="h4"
                                                                    sx={{
                                                                        fontSize: '1rem',
                                                                        fontWeight: 700,
                                                                        color: 'black',
                                                                        lineHeight: 1.2
                                                                    }}
                                                                >
                                                                    {formatCurrency(Number(card?.price))}
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        {/* Book button */}
                                                        <Button
                                                            variant="contained"
                                                            // onClick={() => onBook()}
                                                            // disabled={isBookingLoading || isDisabled}
                                                            sx={{
                                                                px: '1rem',
                                                                py: '0.25rem',
                                                                borderRadius: 1,
                                                                fontSize: '0.75rem',
                                                                fontWeight: 500,
                                                                textTransform: 'none',
                                                                minWidth: 0,
                                                                minHeight: 0,
                                                                height: 40,
                                                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                                            }}
                                                        >
                                                            Check
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Box>
                                </Box>
                            </Card>
                        </Box>
                    )
                })}
                {Array.from({ length: skeletonCount }).map((_, index) => (
                    <SkeletonFlightCard key={`seo-skeleton-${index}`} />
                ))}
            </Box>
        </Box>
    )
}

export default SeoFlightResult
